"use client";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const CLICK_TRACKING_KEY = "rb_contact_clicks";
const CACHE_TIME_MS = 2 * 60 * 60 * 1000; // 7 * 24 * 60 * 60 * 1000 = 7 days in milliseconds
const NOTIFICATION_THRESHOLD = 8;

interface ClickTrackingData {
  count: number;
  timestamp: number;
}

type CrhClickCountingContextType = {
  clickCount: number;
  showNotification: boolean;
  trackClick: () => void;
  resetCount: () => void;
  thresholdReached: boolean;
};

const CrhClickCountingContext =
  createContext<CrhClickCountingContextType | null>(null);

export function CrhClickCountingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [clickCount, setClickCount] = useState<number>(0);
  const [showNotification, setShowNotification] = useState<boolean>(false);

  // Initialize and check if data needs to be reset
  useEffect(() => {
    const storedData = localStorage.getItem(CLICK_TRACKING_KEY);

    const now = Date.now();
    let currentCount = 0;

    if (storedData) {
      const data: ClickTrackingData = JSON.parse(storedData);

      // Check if 7 days have passed since the data was first stored
      if (now - data.timestamp > CACHE_TIME_MS) {
        // Reset the data
        localStorage.removeItem(CLICK_TRACKING_KEY);
        currentCount = 0;
      } else {
        currentCount = data.count;
      }
    }

    setClickCount(currentCount);
    setShowNotification(currentCount >= NOTIFICATION_THRESHOLD);
  }, []);

  // Track a click
  const trackClick = useCallback(() => {
    const now = Date.now();
    const storedData = localStorage.getItem(CLICK_TRACKING_KEY);

    let currentCount = 0;
    let existingTimestamp = now; // Default to now for first click

    if (storedData) {
      const data: ClickTrackingData = JSON.parse(storedData);

      // Check if 7 days have passed since first click
      if (now - data.timestamp > CACHE_TIME_MS) {
        // Reset everything - this is a new tracking period
        const newData: ClickTrackingData = {
          count: 1,
          timestamp: now,
        };
        localStorage.setItem(CLICK_TRACKING_KEY, JSON.stringify(newData));
        setClickCount(1);
        setShowNotification(false);
        return;
      }

      // Use existing data
      currentCount = data.count;
      existingTimestamp = data.timestamp;
    }

    // Increment the click count
    const newCount = currentCount + 1;
    const newData: ClickTrackingData = {
      count: newCount,
      timestamp: existingTimestamp,
    };

    localStorage.setItem(CLICK_TRACKING_KEY, JSON.stringify(newData));
    setClickCount(newCount);

    // Check if we should show notification
    if (newCount >= NOTIFICATION_THRESHOLD) {
      setShowNotification(true);
    }
  }, []);

  // Reset the click count
  const resetCount = useCallback(() => {
    localStorage.removeItem(CLICK_TRACKING_KEY);
    setClickCount(0);
    setShowNotification(false);
  }, []);

  const contextValue = {
    clickCount,
    showNotification,
    trackClick,
    resetCount,
    thresholdReached: clickCount >= NOTIFICATION_THRESHOLD,
  };

  return (
    <CrhClickCountingContext.Provider value={contextValue}>
      {children}
    </CrhClickCountingContext.Provider>
  );
}

export function useCrhClickCountingContext() {
  const context = useContext(CrhClickCountingContext);
  if (!context) {
    throw new Error(
      "useCrhClickCountingContext must be used within a CrhClickCountingProvider",
    );
  }
  return context;
}
